import { GQL } from "@binale-tech/shared";
import { useApolloClient } from "@apollo/client/react";
import { use, useRef, useState } from "react";
import { uuidv7 } from "uuidv7";

import { useDmsAutoSort } from "@dms/hooks/useDmsAutoSort";
import { useDmsContactActions } from "@dms/hooks/useDmsContactActions";
import { BanksContext, BankTransactionsContext } from "@domain/context/BanksContext";
import { RecordsContext } from "@domain/context/accountingData/RecordsCtx";
import { CashRegisterContext, CashRegisterTransactionsContext } from "@domain/context/CashRegisterContext";
import { ContactsContext } from "@domain/context/ContactsContext";
import { CompanyAccountingYearContext, CompanyContext, YearPeriodContext } from "@domain/context/CompanyContext";
import { DmsTypesContext, useDmsDocumentsMap } from "@domain/context/DmsDataContexts";
import { transactionsExportToRecords } from "@domain/context/mutations/bankMutations.graphql";
import { cashRegisterTransactionsCreateFromDocuments } from "@domain/context/mutations/cashRegisterMutations.graphql";
import { genericRecordsAiReview } from "@domain/context/mutations/genericRecordMutations.graphql";
import {
    postingRuleImportSuggestionsMutation,
    postingRuleSuggestionsQuery,
} from "@domain/context/queries/postingRules.graphql";
import {
    accountReconciliationApply,
    bankingFillBank,
    bankingTransactionsCreateContacts,
    bankingTransactionsMatchContacts,
} from "@domain/context/mutations/bankMutations.graphql";
import {
    accountReconciliationAccounts,
    accountReconciliationPlan,
    bankingTransactionsContactSuggestions,
} from "@domain/context/queries/bankQueries.graphql";
import {
    elsterExportsAgentQuery,
    dmsDocumentFindTotalQuery,
    documentsProcessAiMutation,
    pendingWorkQuery,
    recordsAiReviewQuery,
} from "@domain/context/queries/agentQueries.graphql";
import { UserContext } from "@domain/context/UserProvider";
import { mutationDmsDocumentsToRecordsEnqueue, queryAutomationJob } from "@domain/graphql/automationJob";
import { DmsUtils } from "@dms/scripts/utils/DmsUtils";
import { DmsType } from "@domain/models/dms";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { mailboxConfigQuery } from "@dms/features/mailbox/mailbox.graphql";

import { resolveDmsFolder } from "./dmsFolder";
import { resolveUstvaFrequency } from "./ustvaFrequency";
import { MAX_IDS_PER_JOB } from "./steps/draftSelection";
import { exportedTransactionIds } from "./steps/exportedTransactions";
import { sendInChunks } from "./steps/paging";
import { buildCashRegisterFolders } from "./steps/cashRegisterFolders";
import { readInChunks } from "./steps/chunks";
import { findDraftDocuments } from "./steps/findDraftDocuments";
import { AgentRecordsNotLoadedError } from "./runner/errors";
import type { AgentContext } from "./runner/types";

const berlinYear = () =>
    Number(new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric" }).format(new Date()));

/**
 * Builds the context of one run. The object is created once; company and year are fixed at the opening, other live
 * inputs (rights, documents, banks, mutations) are read through refs so a step sees the current value.
 */
export const useAgentContext = (): AgentContext => {
    const { companyGQL } = use(CompanyContext);
    const { year: selectedYear } = use(YearPeriodContext);
    const user = use(UserContext);
    const { banks } = use(BanksContext);
    const documentsMap = useDmsDocumentsMap();
    const client = useApolloClient();
    const autoSort = useDmsAutoSort();
    const { fillContactOutcomes, createContacts } = useDmsContactActions();
    const { documentTypes } = use(DmsTypesContext);
    const { registersById } = use(CashRegisterContext);
    const { transactionMap, transactionRecordsMap } = use(BankTransactionsContext);
    const { transactionMap: kasseTransactionMap, transactionRecordsMap: kasseRecordsMap } = use(
        CashRegisterTransactionsContext,
    );
    const { recordsBank, recordsKB, allRecords } = use(RecordsContext);
    const { yearConfig } = use(CompanyAccountingYearContext);
    const { contactsMap } = use(ContactsContext);

    const live = {
        companyGQL,
        year: selectedYear,
        user,
        banks,
        documentsMap,
        client,
        autoSort,
        fillContactOutcomes,
        createContacts,
        documentTypes,
        registersById,
        transactionMap,
        transactionRecordsMap,
        kasseTransactionMap,
        kasseRecordsMap,
        recordsBank,
        recordsKB,
        allRecords,
        yearConfig,
        contactsMap,
    };
    const liveRef = useRef(live);
    liveRef.current = live;

    const [context] = useState((): AgentContext => {
        // Company and year are fixed at open time for every call of the run; the session remounts per opening.
        const companyId = liveRef.current.companyGQL?.id ?? "";
        const year = liveRef.current.year;
        // The UStVA config of the opening; missing means no UStVA line, never a default cadence.
        const ustvaConfig = liveRef.current.yearConfig ?? null;
        // The records listeners load the selected year and its neighbours; a run year outside that window has no records loaded.
        const requireRecordsWindow = (loaded = true) => {
            if (Math.abs(liveRef.current.year - year) > 1 || !loaded) throw new AgentRecordsNotLoadedError(year);
        };
        return {
            companyId,
            year,
            canWrite: (p) => ProductAccessUtils.canWrite(p, liveRef.current.companyGQL, liveRef.current.user),
            canRead: (p) => ProductAccessUtils.canRead(p, liveRef.current.companyGQL, liveRef.current.user),
            created: { recordIds: [], documentIds: [] },
            documents: () => liveRef.current.documentsMap,
            banks: () => liveRef.current.banks,
            readPendingBuckets: async () => {
                const { data } = await liveRef.current.client.query({
                    query: pendingWorkQuery,
                    variables: { companyId, year },
                    fetchPolicy: "no-cache",
                });
                return data?.pendingWork.buckets ?? [];
            },
            countNewDocuments: async () => {
                const { data } = await liveRef.current.client.query({
                    query: dmsDocumentFindTotalQuery,
                    variables: { input: { companyId, types: [DmsType.new_documents] } },
                    fetchPolicy: "no-cache",
                });
                return data?.dmsDocumentFind.total ?? 0;
            },
            readMailboxConfig: async () => {
                const { data } = await liveRef.current.client.query({
                    query: mailboxConfigQuery,
                    variables: { companyId },
                    fetchPolicy: "no-cache",
                });
                return data?.mailboxConfig ?? null;
            },
            processAi: async (ids) => {
                await liveRef.current.client.mutate({
                    mutation: documentsProcessAiMutation,
                    variables: { input: { companyId, ids } },
                });
            },
            autoSort: (ids) => liveRef.current.autoSort(ids),
            uploadRequested: false,
            autoSortResults: [],
            fillDocumentContacts: (ids) => liveRef.current.fillContactOutcomes(ids),
            createDocumentContacts: (ids) => liveRef.current.createContacts(ids),
            dmsFolder: (type, subType) => resolveDmsFolder(liveRef.current.documentTypes, type, subType),
            cashRegisterFolders: () =>
                buildCashRegisterFolders(liveRef.current.documentTypes, liveRef.current.registersById),
            createKasseFromDocuments: async (cashRegisterId, documentIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: cashRegisterTransactionsCreateFromDocuments,
                    variables: { companyId, cashRegisterId, documentIds },
                });
                return data?.cashRegisterTransactionsCreateFromDocuments ?? [];
            },
            currentYear: () => berlinYear(),
            contactIbans: (contactId) =>
                (liveRef.current.contactsMap.get(contactId)?.banks ?? []).flatMap((b) =>
                    b?.IBAN ? [b.IBAN.replace(/\s+/g, "").toUpperCase()] : [],
                ),
            bankTransactions: () => [...liveRef.current.transactionMap.values()],
            matchBankContacts: async (transactionIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: bankingTransactionsMatchContacts,
                    variables: { companyId, transactionIds },
                });
                return data?.bankingTransactionsMatchContacts ?? [];
            },
            readBankContactSuggestions: async (transactionIds) => {
                const { data } = await liveRef.current.client.query({
                    query: bankingTransactionsContactSuggestions,
                    variables: { companyId, transactionIds },
                    fetchPolicy: "no-cache",
                });
                return data?.bankingTransactionsContactSuggestions ?? [];
            },
            createBankContacts: async (vatIds, transactionIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: bankingTransactionsCreateContacts,
                    variables: { companyId, vatIds, transactionIds },
                });
                return data?.bankingTransactionsCreateContacts ?? [];
            },
            findDraftDocuments: () => findDraftDocuments(liveRef.current.client, companyId, year),
            documentGroupId: (type, subType) =>
                subType ? DmsUtils.getGroupId(type, subType, liveRef.current.documentTypes) : undefined,
            enqueueDocumentsToRecords: async (productKey, groupId, documentIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: mutationDmsDocumentsToRecordsEnqueue,
                    variables: { input: { companyId, documentIds, productKey, groupId: groupId ?? null } },
                });
                const job = data?.dmsDocumentsToRecordsEnqueue;
                if (!job) throw new Error("dmsDocumentsToRecordsEnqueue returned no job");
                return job.id;
            },
            readAutomationJob: async (id) => {
                const { data } = await liveRef.current.client.query({
                    query: queryAutomationJob,
                    variables: { companyId, id },
                    fetchPolicy: "no-cache",
                });
                if (!data?.automationJob) throw new Error("Job not found");
                return data.automationJob;
            },
            cashRegisterTransactions: () => [...liveRef.current.kasseTransactionMap.values()],
            exportedTransactionIds: (kind) => {
                const isBank = kind === GQL.ITransactionExportKind.Bank;
                const records = isBank ? liveRef.current.recordsBank : liveRef.current.recordsKB;
                requireRecordsWindow(records.isGroupListLoaded);
                return exportedTransactionIds(
                    kind,
                    isBank
                        ? [...liveRef.current.transactionRecordsMap.values()].flat()
                        : liveRef.current.kasseRecordsMap.values(),
                );
            },
            exportTransactions: (kind, ids) =>
                sendInChunks(ids, MAX_IDS_PER_JOB, async (transactionIds) => {
                    const { data } = await liveRef.current.client.mutate({
                        mutation: transactionsExportToRecords,
                        variables: { companyId, kind, transactionIds },
                    });
                    return data?.transactionsExportToRecords ?? [];
                }),
            bankRecords: () => {
                requireRecordsWindow(liveRef.current.recordsBank.isGroupListLoaded);
                return liveRef.current.recordsBank.combinedList;
            },
            fillBank: async (action, recordKeys) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: bankingFillBank,
                    variables: { companyId, action, recordKeys },
                });
                return data?.bankingFillBank ?? [];
            },
            readReconciliationAccounts: async () => {
                const { data } = await liveRef.current.client.query({
                    query: accountReconciliationAccounts,
                    variables: { companyId, year },
                    fetchPolicy: "no-cache",
                });
                return data?.accountReconciliationAccounts ?? [];
            },
            planReconciliation: async (konto) => {
                const { data } = await liveRef.current.client.query({
                    query: accountReconciliationPlan,
                    variables: { companyId, konto, mode: GQL.IReconciliationMode.Quick, key: null },
                    fetchPolicy: "no-cache",
                });
                if (!data) throw new Error("accountReconciliationPlan returned no data");
                return data.accountReconciliationPlan;
            },
            applyReconciliation: async (konto, groupIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: accountReconciliationApply,
                    variables: {
                        companyId,
                        konto,
                        mode: GQL.IReconciliationMode.Quick,
                        key: null,
                        groupIds,
                        idempotencyKey: uuidv7(),
                    },
                });
                return data?.accountReconciliationApply ?? [];
            },
            recordInfo: (id) => {
                requireRecordsWindow();
                const record = liveRef.current.allRecords.map.get(id);
                const product = record?.getProductKey();
                return product ? { product } : undefined;
            },
            runAiReview: async (product, ids) => {
                await liveRef.current.client.mutate({
                    mutation: genericRecordsAiReview,
                    variables: { input: { companyId, product, ids } },
                });
            },
            readAiReviews: (product, ids) =>
                readInChunks(ids, async (chunk, limit) => {
                    const { data } = await liveRef.current.client.query({
                        query: recordsAiReviewQuery,
                        variables: {
                            companyId,
                            product,
                            year,
                            ids: chunk,
                            limit,
                        },
                        fetchPolicy: "no-cache",
                    });
                    return (data?.records.records ?? []).map((r) => ({
                        id: String(r.id),
                        aiReview: r.state.aiReview ?? null,
                    }));
                }),
            readPostingRuleSuggestionCount: async (direction) => {
                const { data } = await liveRef.current.client.query({
                    query: postingRuleSuggestionsQuery,
                    variables: { companyId, direction },
                    fetchPolicy: "no-cache",
                });
                return data?.postingRuleSuggestions.length ?? 0;
            },
            runReport: { steps: {}, notRun: [], startedAt: 0 },
            readElsterExports: async () => {
                const { data } = await liveRef.current.client.query({
                    query: elsterExportsAgentQuery,
                    variables: { companyId, year },
                    fetchPolicy: "no-cache",
                });
                return data?.elsterExports ?? [];
            },
            ustvaFrequency: () => resolveUstvaFrequency(ustvaConfig),
            ustvaDauerfrist: () => ustvaConfig?.dauerfristverlaengerung === true,
            importPostingRuleSuggestions: async (direction) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: postingRuleImportSuggestionsMutation,
                    variables: { companyId, direction },
                });
                return (data?.postingRuleImportSuggestions ?? []).map((rule) => ({
                    id: String(rule.id),
                    contactId: String(rule.contactId),
                    counterpartyName: rule.counterpartyName ?? null,
                    categoryNum: rule.categoryNum,
                }));
            },
        };
    });
    return context;
};
